import { useMemo } from 'react'
import useTranslation from '@/hooks/useTranslation'

/**
 * Every piece of user-facing copy in the flow rule panel, translated.
 *
 * It is a hook rather than a constant because translation needs the t()
 * binding. The shape mirrors what RULE_COPY used to be, so call sites read the
 * same — `copy.title`, `copy.tips.idleFlow` — and the panel keeps having one
 * place where its wording lives.
 *
 * Where the wording already existed in Tolgee the existing key is reused
 * rather than duplicated.
 */
export const useRuleCopy = () => {
  const { t } = useTranslation()

  return useMemo(
    () => ({
      title: t('dataConfigFlowrule'),
      defaultsBadge: t('flowRulePanelDefaultsBadge'),
      setUp: t('flowRulePanelSetUp'),
      setUpTip: t('flowRulePanelSetUpTip'),
      remove: t('commonActionsRemove'),
      removeTip: t('flowRulePanelRemoveTip'),
      configure: t('flowRulePanelConfigure'),
      flowAlarm: t('flowRulePanelFlowAlarm'),
      alarmActivated: t('flowRulePanelAlarmActivated'),
      alarmOff: t('flowRulePanelAlarmOff'),
      activatedChip: t('flowRulePanelActivatedChip'),
      disabledChip: t('commonStatusDisabled'),
      alarmDisabled: t('flowRulePanelAlarmDisabledHint'),
      active: t('commonStatusActive'),
      sensorInUse: t('flowRulePanelSensorInUse'),
      sensorNotInUse: t('flowRulePanelSensorNotInUse'),
      automaticAction: t('flowRulePanelAutomaticAction'),
      idleFlow: t('flowRulePanelIdleFlow'),
      lowLimit: t('flowRulePanelLowLimit'),
      highLimit: t('flowRulePanelHighLimit'),
      deviationBand: t('flowRulePanelDeviationBand'),
      idleFlowSection: t('flowRulePanelIdleFlowSection'),
      example: t('flowRulePanelExample'),
      timing: t('flowRulePanelTiming'),
      startDelay: t('commonLabelsStart'),
      startDelayColumn: t('flowRulePanelStartDelayColumn'),
      stopDelayColumn: t('flowRulePanelStopDelayColumn'),
      changeDelay: t('flowRulePanelChangeDelay'),
      errorDelay: t('flowRulePanelErrorDelay'),
      startDelayLabel: t('flowRulePanelStartDelayLabel'),
      changeDelayLabel: t('flowRulePanelChangeDelayLabel'),
      errorDelayLabel: t('flowRulePanelErrorDelayLabel'),
      learning: t('flowRulePanelLearning'),
      stableDeviation: t('flowRulePanelStableDeviation'),
      stableCount: t('flowRulePanelStableCount'),
      stableMax: t('flowRulePanelStableMax'),
      resetError: t('flowRulePanelResetError'),
      monitoringOnly: t('flowRulePanelMonitoringOnly'),
      emptySensorTitle: t('flowRulePanelEmptySensorTitle'),
      emptySensorHint: t('flowRulePanelEmptySensorHint'),
      emptySensorAction: t('flowRulePanelAddSensor'),
      emptyValveTitle: t('flowRulePanelEmptyValveTitle'),
      emptyValveHint: t('flowRulePanelEmptyValveHint'),
      emptyValveAction: t('flowRulePanelAddValve'),
      sensors: t('flowRulePanelSensors'),
      valves: t('flowRulePanelValves'),
      noFlowSensor: t('flowRulePanelFlowSensor'),
      noFlowSensorNone: t('flowRulePanelNone'),
      usage24h: t('flowRulePanelUsage24h'),
      flow: t('commonUnitsFlow'),
      io: t('formsLabelsDecoder'),
      startStop: t('flowRulePanelStartStop'),
      delays: t('flowRulePanelDelays'),
      pickInput: t('flowRulePanelPickInput'),
      pickOutput: t('flowRulePanelPickOutput'),
      // Literal until the Tolgee key exists — Tolgee is the source of truth,
      // so the key is created there before this becomes a `t()` call.
      pickValveType: 'Choose a type',
      pickValveTypeFirst: 'Choose a type for every valve and pump first',
      pickDecoderFirst: 'Choose a decoder for every valve and pump first',
      sensorCountedTip: t('flowRulePanelSensorCountedTip'),
      sensorIgnoredTip: t('flowRulePanelSensorIgnoredTip'),
      noFlowSensorAssigned: t('flowRulePanelNoFlowSensorAssigned'),
      stopDelayLabel: t('flowRulePanelStopDelayLabel'),
      saveRuleFailed: t('flowRulePanelSaveRuleFailed'),
      saveAlarmFailed: t('flowRulePanelSaveAlarmFailed'),
      tips: {
        sensorFlowNow: t('flowRuleTipSensorFlowNow'),
        sensorUsage24h: t('flowRuleTipSensorUsage24h'),
        sensorReadingFresh: t('flowRuleTipSensorReadingFresh'),
        sensorReadingStale: t('flowRuleTipSensorReadingStale'),
        active: t('flowRuleTipActive'),
        automaticAction: t('flowRuleTipAutomaticAction'),
        idleFlow: t('flowRuleTipIdleFlow'),
        lowLimit: t('flowRuleTipLowLimit'),
        highLimit: t('flowRuleTipHighLimit'),
        timing: t('flowRuleTipTiming'),
        deviationBand: t('flowRuleTipDeviationBand'),
        startDelay: t('flowRuleTipStartDelay'),
        changeDelay: t('flowRuleTipChangeDelay'),
        errorDelay: t('flowRuleTipErrorDelay'),
        learning: t('flowRuleTipLearning'),
        stableDeviation: t('flowRuleTipStableDeviation'),
        stableCount: t('flowRuleTipStableCount'),
        stableMax: t('flowRuleTipStableMax'),
        sensors: t('flowRuleTipSensors'),
        valves: t('flowRuleTipValves'),
        noFlowSensor: t('flowRuleTipFlowSensor'),
        priority: t('flowRuleTipPriority'),
        capacity: t('flowRuleTipCapacity'),
        delays: t('flowRuleTipDelays'),
        startDelaySeconds: t('flowRuleTipStartDelaySeconds'),
        stopDelaySeconds: t('flowRuleTipStopDelaySeconds'),
        valveGroup: t('flowRuleTipValveGroup'),
        valveSubType: t('flowRuleTipValveSubType'),
      },
    }),
    [t],
  )
}

export type RuleCopy = ReturnType<typeof useRuleCopy>
